// fleetWire.ts — the browser side of the Fleet Remote CLI wire protocol.
//
// This is the TypeScript mirror of the Go relay's contract in
// server-go/agent_relay.go + server-go/fleet_terminal.go. It is the SINGLE
// source of truth for framing on the browser side; both the Terminal and
// Inference tabs in FleetDetailPage build on it. Keep it byte-compatible with
// the Go encode/decodeDataFrame + ctrlFrame.
//
// Two WS endpoints, both behind the operator session cookie (requireSession):
//   GET /api/v1/fleet/{id}/terminal   — tmux PTY (exclusive lock + read-only
//                                        followers)
//   GET /api/v1/fleet/{id}/infer      — chat/nll/harm relayed to the kit's
//                                        local daemon (0x02 SSE passthrough)
//
// Wire shape (pinned, do NOT deviate — agent_relay.go lines 95-117 + 61-93):
//   control plane = WS TEXT frames, each a single JSON object ("t" tag).
//   data    plane = WS BINARY frames: byte0=kind, bytes[1..2]=sid (uint16 BE),
//                   rest=payload. kind 0x01 = PTY stdin/stdout (both ways),
//                   kind 0x02 = inference response (carries SSE unchanged).
//
// Pre-upgrade refusals: if the kit has no live channel / no pinned device key /
// is not enrolled, the relay answers the GET with a JSON 409 BEFORE upgrading
// (fleet_terminal.go resolveLiveGatedChannel) — so a refusal arrives as a
// failed HTTP handshake (the WS never opens), not a close frame. We surface the
// reason by probing the same path with fetch() when the socket fails to open.

// --- data-plane frame kinds (byte0 of a binary WS frame) --------------------
export const FRAME_KIND_PTY = 0x01; // PTY stdin/stdout, both directions
export const FRAME_KIND_INFER = 0x02; // inference response (incl. SSE passthrough)

// --- control-plane frame ("t"-tagged JSON) ----------------------------------
// A superset across all frame types; only the fields relevant to a given "t"
// are populated. Mirrors agent_relay.go ctrlFrame (the subset the browser ever
// sends or receives — the agent-handshake fields stay server↔kit).
export interface CtrlFrame {
  t: string;
  // ready / deny
  reason?: string;
  // pty_open / pty_resize / pty_close / http / http_end
  sid?: number;
  cols?: number;
  rows?: number;
  // http (browser→relay→kit)
  method?: string;
  path?: string;
  body?: string;
  // http_end (relay→browser)
  status?: number;
}

// encodeDataFrame builds a binary data-plane frame: kind ‖ sid(uint16 BE) ‖
// payload. Mirror of agent_relay.go encodeDataFrame.
export function encodeDataFrame(
  kind: number,
  sid: number,
  payload: Uint8Array,
): Uint8Array {
  const out = new Uint8Array(3 + payload.length);
  out[0] = kind & 0xff;
  out[1] = (sid >> 8) & 0xff;
  out[2] = sid & 0xff;
  out.set(payload, 3);
  return out;
}

// decodeDataFrame splits a binary data-plane frame. Returns null if it is
// shorter than the 3-byte header. Mirror of agent_relay.go decodeDataFrame.
export function decodeDataFrame(
  b: Uint8Array,
): { kind: number; sid: number; payload: Uint8Array } | null {
  if (b.length < 3) return null;
  const kind = b[0];
  const sid = (b[1] << 8) | b[2];
  return { kind, sid, payload: b.subarray(3) };
}

const enc = new TextEncoder();
const dec = new TextDecoder();

export function encodeUtf8(s: string): Uint8Array {
  return enc.encode(s);
}

export function decodeUtf8(b: Uint8Array): string {
  return dec.decode(b);
}

// toUint8 normalises a WS binary payload (which arrives as ArrayBuffer or Blob
// depending on socket.binaryType) into a Uint8Array. We always set
// binaryType = "arraybuffer" on our sockets, so the Blob branch is defensive.
export function arrayBufferToUint8(buf: ArrayBuffer): Uint8Array {
  return new Uint8Array(buf);
}

// wsURL builds the absolute ws(s):// URL for a same-origin relay path. The relay
// legs live under the same origin as the panel (served by sa_server), so we
// derive scheme from window.location. Operator auth rides the session cookie
// (same-origin credentials are sent automatically on the WS upgrade GET).
export function wsURL(path: string, params?: Record<string, string>): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const qs = params
    ? "?" + new URLSearchParams(params).toString()
    : "";
  return `${proto}//${window.location.host}${path}${qs}`;
}

// --- token-count estimation (inference telemetry) --------------------------
// The kit daemon's STREAMING /silicon/chat path (handle_chat_stream in
// arc_silicon_daemon.c) emits OpenAI `chat.completion.chunk` deltas and a final
// chunk whose `arc` block carries the safety verdict/receipt — but NO token
// counts: it strips the engine's own "[mm] …" stats tail and gates output in
// ~24-char blocks, so the wire carries text only. (The NON-streaming path does
// emit a `usage:{completion_tokens,…,approx:true}` block, but the panel always
// requests stream:true.) The browser therefore cannot know the exact token
// count; we ESTIMATE it from the streamed character count and label it as such.
//
// estimateTokens uses the well-known ~4-chars-per-token rule of thumb for
// English BPE vocabularies (GPT-family / Llama tokenizers cluster around it).
// It is intentionally coarse — callers MUST present the result as an estimate
// (e.g. "~48 tokens", "~12 tok/s (est.)"), never as an exact count.
export const CHARS_PER_TOKEN_EST = 4;

export function estimateTokens(charCount: number): number {
  if (charCount <= 0) return 0;
  return Math.max(1, Math.round(charCount / CHARS_PER_TOKEN_EST));
}

// tokenUsageFromArc looks for an EXACT token count on a parsed `arc`/payload
// object, so the UI can prefer truth over the estimate if the daemon ever
// starts passing usage through on the stream (see the report's follow-up note).
// Recognises an OpenAI-style `usage.completion_tokens` and a couple of common
// aliases. Returns null when no usable exact count is present (the case today).
export function tokenUsageFromArc(obj: unknown): number | null {
  if (!obj || typeof obj !== "object") return null;
  const rec = obj as Record<string, unknown>;
  const usage = rec.usage;
  if (usage && typeof usage === "object") {
    const u = usage as Record<string, unknown>;
    // approx:true marks the daemon's own estimate — treat as non-exact.
    if (u.approx !== true) {
      for (const k of ["completion_tokens", "output_tokens", "eval_count"]) {
        const v = u[k];
        if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
      }
    }
  }
  for (const k of ["completion_tokens", "output_tokens", "eval_count"]) {
    const v = rec[k];
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
  }
  return null;
}

// probeRefusalReason re-issues the same relay path as a plain GET to recover the
// human-readable 409 reason the relay emits pre-upgrade (e.g. "CLI unavailable
// (no device key)"). Used when a WS fails to open: browsers do not expose the
// HTTP body of a failed upgrade to onerror/onclose, so we fetch it explicitly.
// Returns null if the probe is inconclusive (network error, or a 2xx that would
// have upgraded). Same-origin so the session cookie is sent.
export async function probeRefusalReason(path: string): Promise<string | null> {
  try {
    const res = await fetch(path, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (res.ok) return null; // a normal endpoint would have upgraded
    const text = await res.text();
    if (text && res.headers.get("content-type")?.includes("application/json")) {
      try {
        const j = JSON.parse(text) as { error?: string };
        if (j.error) return j.error;
      } catch {
        // fall through to status text
      }
    }
    if (res.status === 401) return "Sign in as an operator to use the CLI.";
    return text || `HTTP ${res.status}`;
  } catch {
    return null;
  }
}
